import { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Asset, useListAssetsQuery } from "features/trading/accounts";
import { useDebounce } from "use-debounce";

const MIN_SEARCH_LENGTH = 2;
const ASSET_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 250;

export const getAssetLabel = (asset: Asset) => `${asset.symbol} - ${asset.name}`;

interface AssetAutocompleteProps {
  value: number | "";
  onChange: (assetId: number | "") => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export default function AssetAutocomplete({
  value,
  onChange,
  label = "Asset",
  placeholder = "Search by symbol or name...",
  required = false,
}: AssetAutocompleteProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const trimmedSearch = debouncedSearchTerm.trim();
  const { data: assetsData, isLoading, isFetching } = useListAssetsQuery(
    { search: trimmedSearch, page_size: ASSET_PAGE_SIZE },
    { skip: trimmedSearch.length < MIN_SEARCH_LENGTH },
  );

  const options = useMemo(() => {
    const results = assetsData?.results ?? [];
    if (
      selectedAsset &&
      !results.some((asset) => asset.id === selectedAsset.id)
    ) {
      return [selectedAsset, ...results];
    }
    return results;
  }, [assetsData?.results, selectedAsset]);

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (value === "") {
      setSelectedAsset(null);
      setSearchTerm("");
    }
  }, [value]);

  const clearSelection = () => {
    setSelectedAsset(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <Autocomplete
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={selectedAsset}
      loading={loading}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setSearchTerm(newValue);
          if (selectedAsset) {
            clearSelection();
          }
        } else if (reason === "clear") {
          clearSelection();
        }
      }}
      onChange={(_, asset) => {
        setSelectedAsset(asset);
        onChange(asset?.id ?? "");
        if (asset) {
          setSearchTerm(getAssetLabel(asset));
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        trimmedSearch.length < MIN_SEARCH_LENGTH
          ? "Type at least 2 characters to search"
          : "No assets found"
      }
    />
  );
}
